// src/pages/IssuancePlan/components/ErrorPlanManagementTab.tsx
// Tab "Quản lý kế hoạch lỗi" — bảng "Lịch sử lỗi (JobErrorLog)" mặc định load toàn bộ log lỗi
// sinh QR của mọi kỳ phát hành (có phân trang), khi chọn 1 kỳ ở dropdown thì lọc lại theo đúng kỳ
// đó. Mỗi dòng có icon mắt để xem các dải số (TicketGenerationRangeError) gắn với đúng job của
// dòng log đó và rerun lại riêng từng dải. "Dải" ở đây là dải số vé (numberRangeStart-
// numberRangeEnd theo từng ký hiệu), không phải giải thưởng.
// Chế độ Tự động: nếu toàn bộ các dải của kỳ đang chọn đều lỗi thì rerun lại toàn bộ kỳ
// (sinhBoSoQrApi), ngược lại chỉ rerun từng dải đang lỗi. Chế độ Thủ công: người dùng tự chọn
// rerun từng dải (trong phần mở rộng icon mắt) hoặc rerun toàn bộ kỳ.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import moment from "moment";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";

import {
  getAllQrJobErrorLogsApi,
  getTicketGenerationRangeErrorsApi,
  rerunTicketGenerationRangeErrorApi,
  sinhBoSoQrApi,
} from "helpers/fakebackend_helper";
import { PageActionPermission } from "services/pageActionPermission/PageActionPermission";
import {
  IssuancePlan,
  JobErrorLogEntry,
  TICKET_GENERATION_RANGE_ERROR_STATUS,
  TicketGenerationRangeError,
} from "../types";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('issuance_plan');

interface ErrorPlanManagementTabProps {
  plans: IssuancePlan[];
  // Mục 13.5, REQUIRE_FIXBUGS.md — mở sẵn theo đúng 1 kỳ khi bấm từ badge "Có lỗi" ở bảng danh
  // sách chính, thay vì bắt người dùng tự chọn lại trong dropdown (mặc định "" = xem tất cả).
  initialPlanId?: number;
}

const PAGE_SIZE = 10;

const formatDateTime = (value?: string | null) =>
  value ? moment(value).format("DD/MM/YYYY HH:mm:ss") : "-";

const getPayloadItems = (payload: any) =>
  payload?.items || payload?.data?.items || payload?.data || payload || [];

const ErrorPlanManagementTab: React.FC<ErrorPlanManagementTabProps> = ({
  plans,
  initialPlanId,
}) => {
  const location = useLocation();
  // Bộ lọc theo kỳ phát hành — để trống ("") nghĩa là xem log của TẤT CẢ các kỳ.
  const [planFilter, setPlanFilter] = useState<number | "">(initialPlanId ?? "");
  const [autoMode, setAutoMode] = useState(false);

  const [jobErrorLogs, setJobErrorLogs] = useState<JobErrorLogEntry[]>([]);
  const [loadingJobErrorLogs, setLoadingJobErrorLogs] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Icon mắt: mở popup xem các dải số gắn với đúng job của dòng log đó.
  const [viewLog, setViewLog] = useState<JobErrorLogEntry | null>(null);
  // Click vào cột "Nội dung" (đang bị text-truncate) để xem popup nội dung lỗi đầy đủ.
  const [viewMessageLog, setViewMessageLog] = useState<JobErrorLogEntry | null>(
    null,
  );
  const [expandedRangeErrors, setExpandedRangeErrors] = useState<
    TicketGenerationRangeError[]
  >([]);
  const [loadingExpanded, setLoadingExpanded] = useState(false);
  const [rerunningIds, setRerunningIds] = useState<Set<number>>(new Set());

  // Dải lỗi của cả kỳ đang chọn (không phân biệt theo job nào) — dùng để tính "toàn bộ đều lỗi"
  // và cho nút Rerun tự động / Rerun toàn bộ kỳ ở trên.
  const [planRangeErrors, setPlanRangeErrors] = useState<
    TicketGenerationRangeError[]
  >([]);
  const [rerunningAll, setRerunningAll] = useState(false);

  const planCodeById = useMemo(
    () => new Map(plans.map((plan) => [plan.id, plan.planCode])),
    [plans],
  );

  const loadJobErrorLogs = useCallback(
    async (page: number, targetId: number | "") => {
      setLoadingJobErrorLogs(true);
      try {
        const response = await getAllQrJobErrorLogsApi(
          page,
          PAGE_SIZE,
          targetId === "" ? undefined : Number(targetId),
        );
        const payload = (response as any)?.data || response;
        setJobErrorLogs(getPayloadItems(payload));
        setTotalItems(payload?.totalItems ?? payload?.totalCount ?? 0);
        setTotalPages(payload?.totalPages ?? 0);
      } catch (error: any) {
        toast.error(error?.message || st('khong_the_tai_lich_su_loi_message'));
        setJobErrorLogs([]);
      } finally {
        setLoadingJobErrorLogs(false);
      }
    },
    [],
  );

  useEffect(() => {
    loadJobErrorLogs(pageIndex, planFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, planFilter]);

  // Đổi bộ lọc kỳ thì quay về trang 1 và đóng popup xem dải đang mở (nếu có).
  useEffect(() => {
    setPageIndex(1);
    setViewLog(null);
    setExpandedRangeErrors([]);
  }, [planFilter]);

  const loadPlanRangeErrors = useCallback(async (planId: number) => {
    try {
      const response = await getTicketGenerationRangeErrorsApi(planId);
      const payload = (response as any)?.data || response;
      setPlanRangeErrors(Array.isArray(payload) ? payload : []);
    } catch {
      setPlanRangeErrors([]);
    }
  }, []);

  useEffect(() => {
    if (planFilter === "") {
      setPlanRangeErrors([]);
      return;
    }
    loadPlanRangeErrors(Number(planFilter));
  }, [planFilter, loadPlanRangeErrors]);

  const failedPlanErrors = useMemo(
    () => planRangeErrors.filter((item) => item.status === TICKET_GENERATION_RANGE_ERROR_STATUS.Failed),
    [planRangeErrors],
  );
  const isAllFailed =
    planRangeErrors.length > 0 &&
    failedPlanErrors.length === planRangeErrors.length;

  // Mở popup xem các dải số lỗi gắn với đúng job của dòng log được bấm icon mắt.
  const handleViewRangeErrors = useCallback(async (log: JobErrorLogEntry) => {
    setViewLog(log);
    setExpandedRangeErrors([]);
    setLoadingExpanded(true);
    try {
      const response = await getTicketGenerationRangeErrorsApi(log.targetId);
      const payload = (response as any)?.data || response;
      const items: TicketGenerationRangeError[] = Array.isArray(payload)
        ? payload
        : [];
      setExpandedRangeErrors(items.filter((item) => item.jobId === log.jobId));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_dai_loi_cua_message'));
      setExpandedRangeErrors([]);
    } finally {
      setLoadingExpanded(false);
    }
  }, []);

  const handleRerunOne = useCallback(async (rangeErrorId: number) => {
    setRerunningIds((prev) => new Set(prev).add(rangeErrorId));
    try {
      await rerunTicketGenerationRangeErrorApi(rangeErrorId);
      toast.success(st('da_gui_yeu_cau_rerun_dai_message', { range_error_id: rangeErrorId }));
    } catch (error: any) {
      toast.error(
        error?.message || st('khong_the_rerun_dai_loi_range_message', { range_error_id: rangeErrorId }),
      );
    } finally {
      setRerunningIds((prev) => {
        const next = new Set(prev);
        next.delete(rangeErrorId);
        return next;
      });
    }
  }, []);

  const handleRerunFull = useCallback(async () => {
    if (planFilter === "") return;
    setRerunningAll(true);
    try {
      await sinhBoSoQrApi(Number(planFilter));
      toast.success(st('da_gui_yeu_cau_rerun_lai_message'));
      await loadPlanRangeErrors(Number(planFilter));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_rerun_toan_bo_ky_message'));
    } finally {
      setRerunningAll(false);
    }
  }, [planFilter, loadPlanRangeErrors]);

  const handleAutoRerun = useCallback(async () => {
    if (planFilter === "" || failedPlanErrors.length === 0) {
      toast.info(st('khong_co_dai_loi_nao_can_message'));
      return;
    }

    if (isAllFailed) {
      await handleRerunFull();
      return;
    }

    setRerunningAll(true);
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const item of failedPlanErrors) {
        // eslint-disable-next-line no-await-in-loop
        await rerunTicketGenerationRangeErrorApi(item.id);
      }
      toast.success(st('da_gui_yeu_cau_rerun_length_message', { length: failedPlanErrors.length }));
      await loadPlanRangeErrors(Number(planFilter));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_rerun_tu_dong_cac_message'));
    } finally {
      setRerunningAll(false);
    }
  }, [
    planFilter,
    failedPlanErrors,
    isAllFailed,
    handleRerunFull,
    loadPlanRangeErrors,
  ]);

  return (
    <Card>
      <CardBody>
        <Row className="g-3 align-items-end mb-3">
          <Col md={5}>
            <Label className="form-label">{st('loc_theo_ky_phat_hanh_label')}</Label>
            <Input
              type="select"
              className="form-select"
              value={planFilter}
              onChange={(event) =>
                setPlanFilter(
                  event.target.value ? Number(event.target.value) : "",
                )
              }
            >
              <option value="">{st('tat_ca_cac_ky_option')}</option>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.planCode}
                </option>
              ))}
            </Input>
          </Col>
          <Col md={4}>
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                id="errorPlanAutoModeSwitch"
                data-action-code="input-auto-rerun-mode-switch"
                data-action-name="Chế độ rerun tự động"
                checked={autoMode}
                disabled={
                  !PageActionPermission.canEnable(location.pathname, "input-auto-rerun-mode-switch")
                }
                onChange={(event) => setAutoMode(event.target.checked)}
              />
              <label
                className="form-check-label"
                htmlFor="errorPlanAutoModeSwitch"
              >
                {st('che_do_rerun_tu_dong_label')}{" "}
                <span className="text-muted small">
                  ({autoMode ? st('tu_dong_text') : st('thu_cong_text')})
                </span>
              </label>
            </div>
          </Col>
          <Col md={3} className="text-md-end">
            {autoMode ? (
              PageActionPermission.canDisplay(location.pathname, "button-rerun-auto") && (
                <Button
                  color="primary"
                  disabled={
                    planFilter === "" ||
                    rerunningAll ||
                    failedPlanErrors.length === 0
                  }
                  title={
                    planFilter === ""
                      ? st('chon_mot_ky_phat_hanh_de_text')
                      : undefined
                  }
                  data-action-code="button-rerun-auto"
                  data-action-name="Rerun tự động"
                  onClick={handleAutoRerun}
                >
                  {rerunningAll && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  )}
                  {st('rerun_tu_dong_button')}
                </Button>
              )
            ) : (
              PageActionPermission.canDisplay(location.pathname, "button-rerun-full") && (
                <Button
                  color="warning"
                  disabled={planFilter === "" || rerunningAll}
                  title={
                    planFilter === ""
                      ? st('chon_mot_ky_phat_hanh_de_text')
                      : undefined
                  }
                  data-action-code="button-rerun-full"
                  data-action-name="Rerun toàn bộ kỳ"
                  onClick={handleRerunFull}
                >
                  {rerunningAll && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  )}
                  {st('rerun_toan_bo_ky_button')}
                </Button>
              )
            )}
          </Col>
        </Row>

        {planFilter !== "" && planRangeErrors.length > 0 && (
          <div className="mb-3">
            <Badge color="light" className="text-dark border me-2">
              {st('tong_dai_text')} {planRangeErrors.length}
            </Badge>
            <Badge color="danger-subtle" className="text-danger me-2">
              {st('dang_loi_text')} {failedPlanErrors.length}
            </Badge>
            <Badge color="success-subtle" className="text-success">
              {st('da_xu_ly_text')} {planRangeErrors.length - failedPlanErrors.length}
            </Badge>
            {isAllFailed && (
              <div className="alert alert-danger mt-2 mb-0">
                {st('toan_bo_cac_dai_cua_ky_text')}
              </div>
            )}
          </div>
        )}

        <h6 className="mb-2">{st('lich_su_loi_joberrorlog_title')}</h6>
        <div className="table-responsive table-card mb-1">
          <table className="table align-middle table-nowrap mb-0">
            <thead className="table-light text-muted text-uppercase">
              <tr>
                <th>{st('ky_column')}</th>
                <th>{st('nguon_loi_column')}</th>
                <th>{st('noi_dung_column')}</th>
                <th>{st('thoi_gian_column')}</th>
                <th style={{ width: 60 }}>{st('xem_column')}</th>
              </tr>
            </thead>
            <tbody>
              {loadingJobErrorLogs ? (
                Array.from({ length: 4 }).map((_, row) => (
                  <tr key={row}>
                    {Array.from({ length: 5 }).map((__, col) => (
                      <td key={col}>
                        <div className="placeholder-glow">
                          <span className="placeholder col-8"></span>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : jobErrorLogs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-5">
                    <div className="mb-2">
                      <i className="ri-checkbox-circle-line fs-1 text-success"></i>
                    </div>
                    {st('khong_co_lich_su_loi_job_text')}
                  </td>
                </tr>
              ) : (
                jobErrorLogs.map((log) => (
                  <tr key={log.id}>
                    <td>
                      {planCodeById.get(log.targetId) ||
                        st('ky_target_id_text', { target_id: log.targetId })}
                    </td>
                    <td>
                      <span
                        className={`fw-semibold ${log.errorSource === "Stalled" ? "text-warning" : "text-danger"}`}
                      >
                        {log.errorSource === "Stalled"
                          ? st('dung_dot_ngot_text')
                          : st('loi_text')}
                      </span>
                    </td>
                    <td>
                      <div
                        className="text-truncate"
                        style={{ maxWidth: 400, cursor: "pointer" }}
                        role="button"
                        title={st('xem_noi_dung_loi_day_du_title')}
                        onClick={() => setViewMessageLog(log)}
                      >
                        {log.errorMessage}
                      </div>
                    </td>
                    <td>{formatDateTime(log.occurredAt)}</td>
                    <td>
                      {PageActionPermission.canDisplay(location.pathname, "button-view-range-errors") && (
                        <Button
                          color="light"
                          size="sm"
                          className="btn-icon"
                          title={st('xem_cac_dai_so_cua_job_title')}
                          data-action-code="button-view-range-errors"
                          data-action-name="Xem dải số lỗi"
                          onClick={() => handleViewRangeErrors(log)}
                        >
                          <i className="ri-eye-line"></i>
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalItems > 0 && (
          <Row className="mt-3 align-items-center">
            <Col md={6}>
              <span className="text-muted">
                {st('trang_text')} {pageIndex}
                {totalPages ? ` / ${totalPages}` : ""} {st('tong_text')} {totalItems}{" "}
                {st('ban_ghi_text')}
              </span>
            </Col>
            <Col md={6}>
              <div className="d-flex justify-content-end gap-2">
                <Button
                  size="sm"
                  color="light"
                  disabled={pageIndex <= 1}
                  onClick={() => setPageIndex((prev) => prev - 1)}
                >
                  {st('truoc_button')}
                </Button>
                <Button
                  size="sm"
                  color="light"
                  disabled={totalPages > 0 && pageIndex >= totalPages}
                  onClick={() => setPageIndex((prev) => prev + 1)}
                >
                  {st('sau_button')}
                </Button>
              </div>
            </Col>
          </Row>
        )}
      </CardBody>

      <Modal
        isOpen={!!viewLog}
        toggle={() => setViewLog(null)}
        centered
        size="lg"
      >
        <ModalHeader className="bg-light p-3" toggle={() => setViewLog(null)}>
          {st('dai_loi_title')}{" "}
          {viewLog
            ? planCodeById.get(viewLog.targetId) || st('ky_target_id_text', { target_id: viewLog.targetId })
            : ""}
        </ModalHeader>
        <ModalBody>
          {loadingExpanded ? (
            <div className="text-center py-4">
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
            </div>
          ) : expandedRangeErrors.length === 0 ? (
            <div className="text-muted small">
              {st('job_nay_chua_ghi_nhan_dai_text')}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead className="text-muted text-uppercase">
                  <tr>
                    <th>{st('ky_hieu_column')}</th>
                    <th>{st('dai_so_column')}</th>
                    <th>{st('loi_text')}</th>
                    <th>{st('trang_thai_column')}</th>
                    <th style={{ width: 100 }}>{st('thao_tac_column')}</th>
                  </tr>
                </thead>
                <tbody>
                  {expandedRangeErrors.map((item) => (
                    <tr key={item.id}>
                      <td>{item.symbol}</td>
                      <td>
                        {item.rangeStart} - {item.rangeEnd}
                      </td>
                      <td className="text-break" style={{ maxWidth: 280 }}>
                        {item.errorMessage}
                      </td>
                      <td>
                        <span
                          className={`badge bg-${item.status === TICKET_GENERATION_RANGE_ERROR_STATUS.Failed ? "danger" : "success"}-subtle text-${item.status === TICKET_GENERATION_RANGE_ERROR_STATUS.Failed ? "danger" : "success"}`}
                        >
                          {item.status === TICKET_GENERATION_RANGE_ERROR_STATUS.Failed ? st('loi_text') : st('da_xu_ly_text_2')}
                        </span>
                      </td>
                      <td>
                        {item.status === TICKET_GENERATION_RANGE_ERROR_STATUS.Failed &&
                          PageActionPermission.canDisplay(location.pathname, "button-rerun-one") && (
                          <Button
                            color="info"
                            outline
                            size="sm"
                            disabled={rerunningIds.has(item.id)}
                            data-action-code="button-rerun-one"
                            data-action-name="Rerun dải này"
                            onClick={() => handleRerunOne(item.id)}
                          >
                            {rerunningIds.has(item.id) ? (
                              <i className="ri-loader-4-line"></i>
                            ) : (
                              st('rerun_dai_nay_text')
                            )}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={!!viewMessageLog}
        toggle={() => setViewMessageLog(null)}
        centered
      >
        <ModalHeader
          className="bg-light p-3"
          toggle={() => setViewMessageLog(null)}
        >
          {st('chi_tiet_loi_title')}{" "}
          {viewMessageLog
            ? planCodeById.get(viewMessageLog.targetId) ||
              st('ky_target_id_text', { target_id: viewMessageLog.targetId })
            : ""}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label className="form-label text-muted mb-1">{st('nguon_loi_column')}</Label>
            <div>
              <span
                className={`fw-semibold ${viewMessageLog?.errorSource === "Stalled" ? "text-warning" : "text-danger"}`}
              >
                {viewMessageLog?.errorSource === "Stalled"
                  ? st('dung_dot_ngot_text')
                  : st('loi_text')}
              </span>
            </div>
          </div>
          <div className="mb-3">
            <Label className="form-label text-muted mb-1">{st('thoi_gian_column')}</Label>
            <div>{formatDateTime(viewMessageLog?.occurredAt)}</div>
          </div>
          <div className="mb-3">
            <Label className="form-label text-muted mb-1">{st('job_id_label')}</Label>
            <div className="text-break">{viewMessageLog?.jobId}</div>
          </div>
          <div>
            <Label className="form-label text-muted mb-1">{st('noi_dung_loi_label')}</Label>
            <div className="text-break">{viewMessageLog?.errorMessage}</div>
          </div>
        </ModalBody>
      </Modal>
    </Card>
  );
};

export default ErrorPlanManagementTab;
